import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../LoadingSpinner";
import Input from "../../input/Input";
import Dropdown from "../../input/Dropdown";
import { FORMS, MODULES } from "../../../const";
import CheckBox from "../../input/CheckBox";
import DaySelector from "../../DaySelector";
import { errorActions } from "../../../store/error-slice";
import useFormVisibility from "../../../hooks/useFormVisibility";
import useFieldVisibility from "../../../hooks/useFieldVisibility";
import {
  summaryInfoValidation,
  validateSummaryInfo,
} from "../../validations/validateRequestCreation";
import useDynamicValidation from "../../../hooks/useDynamicValidation";

const FORM = FORMS.REQUEST.SUMMARY.form;

const RequestSummaryInformation = forwardRef(
  ({ dropdown, editMode, isLoading, opeType, useDynamicFields }, ref) => {
    const dispatch = useDispatch();
    const customerId = useSelector((state) => state.request.reqLCustomerId);

    const isFormVisibilityLoading = useSelector(
      (state) => state.formVisibility.loading,
    );

    const formVisMap = useSelector((state) => state.formVisibility);

    useFormVisibility({
      form: FORM,
      keyValue: customerId ? "stakeholder" : "default",
      type: customerId ?? 1,
      useDynamicFields: true,
      opeType,
    });

    const { visible: isSchedulerVisbile } = useFieldVisibility({
      form: FORM,
      field: "request_scheduler",
      useDynamicFields,
    });

    const dynamicValidationStatus = useDynamicValidation(
      summaryInfoValidation,
      FORM,
      true,
    );

    const viewOnlyMode = useSelector((state) => state.request.viewOnlyMode);
    const reqCbm = useSelector((state) => state.request.reqCbm);
    const reqQty = useSelector((state) => state.request.reqQty);
    const currentValuesSummery = useSelector(
      (state) => state.request.currentValues.requestSummary,
    );

    const scheduleResult = useMemo(
      () => currentValuesSummery?.requestSchedulerResponse ?? {},
      [],
    );

    const [requestSummary, setRequestSummary] = useState(
      editMode
        ? currentValuesSummery
        : {
            reqQty: null,
            reqUom: null,
            reqCbm: null,
            reqCbmUom: null,
            reqTotalKm: null,
            reqApprovedBy: "",
            reqRemarks: "",
            reqApprovedDate: "",
            reqApprovedDateFormatted: "",
            reqStatus: 1,
          },
    );

    useEffect(() => {
      if (editMode) {
        setRequestSummary(currentValuesSummery);
      }
    }, [currentValuesSummery]);

    const [toDateGap, setToDateGap] = useState(null);

    const today = new Date().toISOString().split("T")[0];
    const dateGap = toDateGap || "";
    const [requestScheduler, setRequestScheduler] = useState(
      editMode === true && scheduleResult !== null
        ? {
            ...scheduleResult,
            // rscFrom:scheduleResult.rscFrom.includes("T") ? scheduleResult.rscFrom.split("T")[0] : scheduleResult.rscFrom,
            // rscTo:scheduleResult.rscTo.includes("T") ? scheduleResult.rscTo.split("T")[0] : scheduleResult.rscTo,
            rscFrom: scheduleResult.rscFrom?.includes("T")
              ? scheduleResult.rscFrom.split("T")[0]
              : scheduleResult.rscFrom,
            rscTo: scheduleResult.rscTo?.includes("T")
              ? scheduleResult.rscTo.split("T")[0]
              : scheduleResult.rscTo,
          }
        : {
            rscReferance: "",
            rscComId: 1,
            rscScheduleType: null,
            rscFrom: "",
            rscTo: "",
            rscTimeEnable: false,
            rscStartTime: "09:00",
            rscEndTime: "",
            rscFrequency: 1,
            rscUom: null,
            rscSun: null,
            rscMon: null,
            rscTue: null,
            rscWed: null,
            rscThu: null,
            rscFri: null,
            rscSat: null,
            rscDescription: "",
            rscStatus: 2,
            rscActive: 1,
            rscScheduleTypeName: "",
            minToDate: today,
          },
    );

    const [currentErrors, setCurrentErrors] = useState({});
    const [requestCurrentErrors, setRequestCurrentErrors] = useState({});
    const [selectedDays, setSelectedDays] = useState([]);
    const [editVal, setEditVal] = useState([]);

    const errors = useSelector((state) => state.request.requestSummaryErrors);
    const requestErrors = useSelector(
      (state) => state.error.requestSchedulerErrors,
    );

    useEffect(() => {
      setCurrentErrors(errors);
    }, [errors]);

    useEffect(() => {
      setRequestCurrentErrors(requestErrors.requestSchedulerErrors);
    }, [requestErrors]);

    useEffect(() => {
      setRequestSummary((prevState) => ({
        ...prevState,
        reqCbm: reqCbm,
        reqQty: reqQty,
      }));
    }, [reqCbm, reqQty]);

    useEffect(() => {
      const sunday = 0;
      const monday = 1;
      const tuesday = 2;
      const wednesday = 3;
      const thursday = 4;
      const friday = 5;
      const saturday = 6;
      setRequestScheduler((prevState) => ({
        ...prevState,
        rscSun: selectedDays.includes(sunday),
        rscMon: selectedDays.includes(monday),
        rscTue: selectedDays.includes(tuesday),
        rscWed: selectedDays.includes(wednesday),
        rscThu: selectedDays.includes(thursday),
        rscFri: selectedDays.includes(friday),
        rscSat: selectedDays.includes(saturday),
      }));
    }, [selectedDays]);

    useEffect(() => {
      setEditVal(scheduleResult);
    }, [editMode, scheduleResult]);

    useImperativeHandle(ref, () => ({
      getData: () => requestSummary,
      getRequestSchedulerData: () => requestScheduler,
      validateForm: () => {
        return validateSummaryInfo(
          { ...requestSummary },
          dynamicValidationStatus,
        );
      },
    }));

    const addToDateGap = (data) => {
      const fromDate = new Date(data);
      if (requestScheduler.rscScheduleType === 3) {
        fromDate.setDate(fromDate.getDate() + 7);
        setToDateGap(fromDate.toISOString().split("T")[0]);
      } else if (requestScheduler.rscScheduleType === 4) {
        fromDate.setMonth(fromDate.getMonth() + 1);
        setToDateGap(fromDate.toISOString().split("T")[0]);
      }
    };

    return (
      <>
        {isLoading || isFormVisibilityLoading ? (
          <LoadingSpinner />
        ) : (
          <form className="ui form">
            <div className="ui segment">
              <h4 className="ui dividing header">Summary Information</h4>
              <div className="field">
                <div className="two fields ">
                  <Input
                    required={true}
                    useDynamicFields={true}
                    label="CBM"
                    error={currentErrors.reqCbm}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="req_cbm"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reqCbm: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    name="cbm"
                    readOnly={true}
                    value={requestSummary?.reqCbm}
                  />
                  <Input
                    label="QTY"
                    useDynamicFields={true}
                    error={currentErrors.reqQty}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="req_qty"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reqQty: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    name="remarks"
                    readOnly
                    value={requestSummary?.reqQty}
                  />
                </div>
                <div className="two fields ">
                  <Dropdown
                    readOnly={viewOnlyMode}
                    useDynamicFields={true}
                    label="UOM"
                    error={currentErrors.reqUom}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="req_uom"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reqUom: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    options={dropdown.uomTypeOptions}
                    placeholder="UOM"
                    value={requestSummary?.reqUom}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setRequestSummary((prevState) => ({
                        ...prevState,
                        reqUom: data.value,
                      }));
                    }}
                  />
                  <Input
                    readOnly={viewOnlyMode}
                    useDynamicFields={true}
                    label="Total KM"
                    error={currentErrors.reqTotalKm}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="req_tota_km"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reqTotalKm: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    name="total_km"
                    value={requestSummary?.reqTotalKm}
                    onChange={(e) => {
                      setRequestSummary((prevState) => ({
                        ...prevState,
                        reqTotalKm: e.target.value,
                      }));
                    }}
                  />
                </div>
                <div className="two fields ">
                  <Input
                    readOnly={viewOnlyMode}
                    useDynamicFields={true}
                    label="Remarks"
                    error={currentErrors.reqRemarks}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="req_remarks"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reqRemarks: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    name="remarks"
                    value={requestSummary?.reqRemarks}
                    onChange={(e) => {
                      setRequestSummary((prevState) => ({
                        ...prevState,
                        reqRemarks: e.target.value,
                      }));
                    }}
                  />
                  <Dropdown
                    label="Status"
                    useDynamicFields={true}
                    error={currentErrors.reqStatus}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="req_status"
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { reqStatus: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Status"
                    options={dropdown.statusTypeOptions}
                    style={{ height: "5px" }}
                    value={requestSummary?.reqStatus}
                    readOnly
                  />
                </div>
                {editMode && (
                  <div className="two fields ">
                    <Input
                      label="Approved By"
                      useDynamicFields={true}
                      error={currentErrors.reqApprovedBy}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_approved_by"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqApprovedBy: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="approved_by"
                      value={requestSummary?.reqApprovedBy}
                      readOnly
                    />
                    <Input
                      label="Approved Date Time"
                      useDynamicFields={true}
                      error={currentErrors.reqApprovedDateFormatted}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_approved_date"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqApprovedDateFormatted: _, ...rest } =
                            prevState;
                          return rest;
                        })
                      }
                      name="approved_date"
                      value={requestSummary?.reqApprovedDateFormatted}
                      readOnly
                    />
                  </div>
                )}
              </div>
            </div>
            {isSchedulerVisbile && (
              <div className="ui segment">
                <h4 className="ui dividing header">Scheduler</h4>
                <div className="three fields">
                  <Dropdown
                    label={"Scheduler Type"}
                    useDynamicFields={true}
                    placeholder={"Scheduler Type"}
                    value={requestScheduler.rscScheduleType}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rsc_schedule_type"
                    options={dropdown.scheduleTypeOptions}
                    onChange={(e, data) => {
                      setRequestScheduler((prevState) => ({
                        ...prevState,
                        rscScheduleType: !data.value ? null : data.value,
                        rscUom: null,
                        rscTo: "",
                        rscFrom: "",
                        rscStartTime: "09:00",
                        rscFrequency: 1,
                        rscTimeEnable: false,
                      }));
                      dispatch(errorActions.clearRequestSchedulerError());
                    }}
                    editMode={editMode}
                  />
                  <Input
                    label={"From Date"}
                    useDynamicFields={true}
                    type="date"
                    value={requestScheduler.rscFrom}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rsc_from"
                    onChange={(e) => {
                      setRequestScheduler((prev) => ({
                        ...prev,
                        rscFrom: e.target.value,
                      }));
                      addToDateGap(e.target.value);
                    }}
                    error={requestCurrentErrors.rscFrom}
                    resetError={() =>
                      setRequestCurrentErrors((prevState) => {
                        const { rscFrom: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    editMode={editMode}
                    required={requestScheduler.rscScheduleType}
                    min={requestScheduler.minToDate}
                  />
                  <Input
                    label={"To Date"}
                    useDynamicFields={true}
                    type={"date"}
                    value={requestScheduler.rscTo}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rsc_to"
                    onChange={(e) => {
                      setRequestScheduler((prev) => ({
                        ...prev,
                        rscTo: e.target.value,
                      }));
                    }}
                    error={requestCurrentErrors.rscTo}
                    resetError={() =>
                      setRequestCurrentErrors((prevState) => {
                        const { rscTo: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    editMode={editMode}
                    disabled={requestScheduler.rscScheduleType === 1}
                    required={
                      requestScheduler.rscScheduleType &&
                      requestScheduler.rscScheduleType !== 1
                    }
                    min={dateGap}
                    //test
                  />
                </div>

                <div className="two fields">
                  <Input
                    useDynamicFields={true}
                    type={"number"}
                    min={1}
                    max={30}
                    value={requestScheduler.rscFrequency}
                    form={FORM}
                    module={MODULES.Transaction}
                    field={
                      requestScheduler.rscScheduleType === 4
                        ? "rsc_execution_date"
                        : "rsc_frequency"
                    }
                    onChange={(e) =>
                      setRequestScheduler((prev) => ({
                        ...prev,
                        rscFrequency: e.target.value,
                      }))
                    }
                    editMode={editMode}
                    disabled={requestScheduler.rscScheduleType !== 4}
                    required={requestScheduler.rscScheduleType}
                  />
                  <Dropdown
                    label={"UOM"}
                    useDynamicFields={true}
                    placeholder={"UOM"}
                    options={dropdown.timeUomOptions}
                    value={requestScheduler.rscUom}
                    form={FORM}
                    module={MODULES.Transaction}
                    field={
                      requestScheduler.rscScheduleType === 4
                        ? "rsc_uom_man"
                        : "rsc_uom"
                    }
                    onChange={(e, data) => {
                      setRequestScheduler((prevState) => ({
                        ...prevState,
                        rscUom: data.value,
                      }));
                    }}
                    error={requestCurrentErrors.rscUom}
                    resetError={() =>
                      setRequestCurrentErrors((prevState) => {
                        const { rscUom: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    editMode={editMode}
                    disabled={
                      requestScheduler.rscScheduleType === 1 ||
                      requestScheduler.rscScheduleType === 2 ||
                      requestScheduler.rscScheduleType === 3
                    }
                    required={requestScheduler.rscScheduleType === 4}
                  />
                </div>

                <div className="ui segment">
                  <div className="three fields">
                    <CheckBox
                      label={"isEnabled"}
                      useDynamicFields={true}
                      checked={requestScheduler.rscTimeEnable}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="rsc_time_enable"
                      onChange={() =>
                        setRequestScheduler((prev) => ({
                          ...prev,
                          rscTimeEnable: !prev.rscTimeEnable,
                          rscStartTime: "09:00",
                        }))
                      }
                      editMode={editMode}
                    />
                    <Input
                      label={"Start Time"}
                      useDynamicFields={true}
                      type="time"
                      placeholder={"Start Time"}
                      value={requestScheduler.rscStartTime}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="rsc_start_time"
                      onChange={(e) => {
                        setRequestScheduler((prev) => ({
                          ...prev,
                          rscStartTime: e.target.value,
                        }));
                      }}
                      editMode={editMode}
                      disabled={!requestScheduler.rscTimeEnable}
                    />
                    {/*<DateTimePickerInput*/}
                    {/*    label={"End Time"}*/}
                    {/*    type={"date"}*/}
                    {/*    value={requestScheduler.rscEndTime}*/}
                    {/*    onAccept={(value) => {*/}
                    {/*        setRequestScheduler((prev) => ({*/}
                    {/*            ...prev,*/}
                    {/*            rscEndTime: value,*/}
                    {/*        }))*/}
                    {/*    }}*/}
                    {/*    editMode={editMode}*/}
                    {/*    disabled={true}*/}
                    {/*/>*/}
                  </div>
                </div>
                {requestScheduler.rscScheduleType === 1 ||
                requestScheduler.rscScheduleType === 2 ||
                requestScheduler.rscScheduleType === 4 ? null : (
                  <div className="ui segment">
                    <DaySelector
                      selectedDays={selectedDays}
                      setSelectedDays={setSelectedDays}
                      editVal={editVal}
                    />
                  </div>
                )}
                <div className="three fields">
                  <Input
                    label={"Description"}
                    useDynamicFields={true}
                    type={"text"}
                    value={requestScheduler.rscDescription}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rsc_description"
                    onChange={(e) =>
                      setRequestScheduler((prev) => ({
                        ...prev,
                        rscDescription: e.target.value,
                      }))
                    }
                    editMode={editMode}
                  />
                  <Dropdown
                    label={"Status"}
                    useDynamicFields={true}
                    options={dropdown.statusTypeOptions}
                    value={requestScheduler.rscStatus}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rsc_status"
                    onChange={(e, data) => {
                      setRequestScheduler((prevState) => ({
                        ...prevState,
                        rscStatus: data.value,
                      }));
                    }}
                    error={requestCurrentErrors.rscStatus}
                    resetError={() =>
                      setRequestCurrentErrors((prevState) => {
                        const { rscStatus: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    editMode={editMode}
                    required={requestScheduler.rscScheduleType}
                  />
                  <Dropdown
                    label={"Active"}
                    useDynamicFields={true}
                    options={dropdown.activeOptions}
                    value={requestScheduler.rscActive}
                    form={FORM}
                    module={MODULES.Transaction}
                    field="rsc_active"
                    onChange={(e, data) => {
                      setRequestScheduler((prevState) => ({
                        ...prevState,
                        rscActive: data.value,
                      }));
                    }}
                    error={requestCurrentErrors.rscActive}
                    resetError={() =>
                      setRequestCurrentErrors((prevState) => {
                        const { rscActive: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    editMode={editMode}
                    required={requestScheduler.rscScheduleType}
                  />
                </div>
              </div>
            )}
          </form>
        )}
      </>
    );
  },
);

export default RequestSummaryInformation;
